import React, { useState, useEffect } from 'react';
import { X, Loader2, Cloud, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { initiateOneDriveConnection } from '../services/oneDriveService';

const OneDriveConnectModal = ({ isOpen, onClose, brand, onSuccess }) => {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [step, setStep] = useState('idle'); // 'idle' | 'connecting' | 'success'

    const handleConnect = async () => {
        if (!currentUser || !brand) {
            setError('Missing user or brand information');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setStep('connecting');

            // Request OAuth URL from backend
            const { authUrl } = await initiateOneDriveConnection(currentUser.uid, brand.id);

            // Open OAuth flow in popup
            const width = 600;
            const height = 700;
            const left = (window.screen.width / 2) - (width / 2);
            const top = (window.screen.height / 2) - (height / 2);

            const popup = window.open(
                authUrl,
                'onedrive-oauth',
                `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
            );

            // Listen for OAuth completion
            const handleMessage = async (event) => {
                if (event.data.type === 'oauth-success' && event.data.platform === 'onedrive') {
                    console.log('OneDrive OAuth successful:', event.data);
                    window.removeEventListener('message', handleMessage);
                    setStep('success');
                    setLoading(false);

                    // Notify parent of success after brief delay
                    setTimeout(() => {
                        if (onSuccess) onSuccess(event.data.connectionId);
                        onClose(true);
                    }, 1500);

                } else if (event.data.type === 'oauth-error') {
                    console.error('OneDrive OAuth error:', event.data.error);
                    setError(event.data.error);
                    setStep('idle');
                    setLoading(false);
                    window.removeEventListener('message', handleMessage);
                }
            };

            window.addEventListener('message', handleMessage);

            // Check if popup was blocked
            if (!popup || popup.closed || typeof popup.closed === 'undefined') {
                throw new Error('Popup was blocked. Please allow popups for this site.');
            }

            // Monitor popup closure
            const checkClosed = setInterval(() => {
                try {
                    if (popup.closed) {
                        clearInterval(checkClosed);
                        window.removeEventListener('message', handleMessage);
                        if (step === 'connecting') {
                            setStep('idle');
                            setLoading(false);
                        }
                    }
                } catch (e) {
                    clearInterval(checkClosed);
                }
            }, 500);

        } catch (err) {
            console.error('OneDrive connection error:', err);
            setError(err.message);
            setStep('idle');
            setLoading(false);
        }
    };

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setError(null);
            setLoading(false);
            setStep('idle');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(4px)'
            }}
            onClick={() => step === 'idle' && onClose()}
        >
            <div
                className="card"
                style={{
                    maxWidth: '480px',
                    width: '90%',
                    position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--space-6)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: 'var(--radius-lg)',
                            background: 'linear-gradient(135deg, #0078D4 0%, #005A9E 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Cloud size={24} color="white" />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, marginBottom: 'var(--space-1)' }}>
                                Connect OneDrive
                            </h2>
                            <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                {brand?.name}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--space-2)' }}
                        disabled={loading}
                    >
                        <X size={20} />
                    </button>
                </div>

                {step === 'success' ? (
                    <div style={{
                        textAlign: 'center',
                        padding: 'var(--space-8)'
                    }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '50%',
                            background: 'var(--color-success-bg)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto var(--space-4)'
                        }}>
                            <CheckCircle size={32} color="var(--color-success)" />
                        </div>
                        <h3 style={{ margin: 0, marginBottom: 'var(--space-2)' }}>
                            OneDrive Connected!
                        </h3>
                        <p className="text-secondary" style={{ margin: 0 }}>
                            Your Microsoft account has been successfully connected.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Error Message */}
                        {error && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    background: 'var(--color-error-bg)',
                                    color: 'var(--color-error)',
                                    padding: 'var(--space-3)',
                                    borderRadius: 'var(--radius-md)',
                                    marginBottom: 'var(--space-4)',
                                    border: '1px solid var(--color-error)',
                                    fontSize: 'var(--font-size-sm)'
                                }}
                            >
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}

                        {/* Description */}
                        <p className="text-secondary" style={{
                            margin: 0,
                            marginBottom: 'var(--space-6)',
                            lineHeight: 1.6
                        }}>
                            Connect your Microsoft OneDrive account to browse and link images directly to your asset library. Your files stay in OneDrive - we just display previews.
                        </p>

                        {/* Connect Button */}
                        <button
                            className="btn btn-primary"
                            onClick={handleConnect}
                            disabled={loading}
                            style={{
                                width: '100%',
                                padding: 'var(--space-3) var(--space-4)',
                                background: 'linear-gradient(135deg, #0078D4 0%, #005A9E 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 'var(--space-2)'
                            }}
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={18} className="spinning" />
                                    Connecting...
                                </>
                            ) : (
                                <>
                                    <Cloud size={18} />
                                    Connect with Microsoft
                                </>
                            )}
                        </button>

                        {/* Info */}
                        <div
                            style={{
                                marginTop: 'var(--space-6)',
                                padding: 'var(--space-4)',
                                background: 'var(--color-bg-tertiary)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-border)'
                            }}
                        >
                            <p className="text-secondary" style={{
                                margin: 0,
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                We request read-only access to view your files and thumbnails. Your credentials are encrypted and securely stored.
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default OneDriveConnectModal;
